import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LiveBadge from './LiveBadge.jsx'

/**
 * HLS player for MediaMTX streams (later: WebRTC/WHEP for sub-second latency).
 * Without a working stream it shows the poster with a short note.
 */
export default function LivePlayer({ src, poster, alt, camLabel, viewers }) {
  const { t } = useTranslation()
  const videoRef = useRef(null)
  const [failedSrc, setFailedSrc] = useState(null)
  const playing = Boolean(src) && failedSrc !== src

  useEffect(() => {
    const video = videoRef.current
    if (!src || !video) return

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src // Safari / iOS: native HLS
      return
    }
    let hls
    let cancelled = false
    // hls.js is heavy — load it only when a stream is actually shown.
    import('hls.js').then(({ default: Hls }) => {
      if (cancelled) return
      if (!Hls.isSupported()) return setFailedSrc(src)
      hls = new Hls({ liveDurationInfinity: true, lowLatencyMode: true })
      hls.on(Hls.Events.ERROR, (_, data) => data.fatal && setFailedSrc(src))
      hls.loadSource(src)
      hls.attachMedia(video)
    })
    return () => {
      cancelled = true
      hls?.destroy()
    }
  }, [src])

  return (
    <div className="relative aspect-video overflow-hidden rounded-3xl bg-slate">
      {poster && <img src={poster} alt={alt} className="absolute inset-0 size-full object-cover" />}
      {playing && (
        <video
          ref={videoRef}
          className="absolute inset-0 size-full object-cover"
          poster={poster}
          autoPlay
          muted
          playsInline
          controls
          onError={() => setFailedSrc(src)}
        />
      )}
      {playing ? (
        <div className="pointer-events-none absolute top-5 left-5 flex gap-2">
          <LiveBadge />
          {viewers > 0 && (
            <span className="rounded-full bg-black/45 px-3 py-1.5 text-[13px] font-bold text-white">
              {t('live.watching', { count: viewers })}
            </span>
          )}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-scrim/55 p-6 text-center">
          <span className="rounded-full bg-black/45 px-4 py-2 text-[15px] font-bold text-white">
            {src ? t('live.streamOffline') : t('live.noStream')}
          </span>
        </div>
      )}
      {camLabel && (
        <span className="pointer-events-none absolute right-5 bottom-5 rounded-full bg-black/45 px-3 py-1.5 text-[13px] font-bold text-white">
          {camLabel}
        </span>
      )}
    </div>
  )
}
